import { Component, OnInit, OnDestroy } from '@angular/core';
import { NgClass } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faHome,
  faChevronLeft,
  faChevronRight,
  faCreditCard,
  faGear,
} from '@fortawesome/pro-regular-svg-icons';
import { faBarsSort } from '@fortawesome/pro-regular-svg-icons';
import { faUser as faUserRegular } from '@fortawesome/pro-regular-svg-icons';
import { CollapsibleMenuStateService } from '@core/services/collapsible-menu-state.service';
import { AuthenticationService } from '@core/services/auth.service';
import { SidebarService } from '@core/services/side-bar.service';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { NavigationShellStateService } from '@core/services/navigation-shell-state.service';
import { SidebarNavigationGroupComponent } from '@shared/sidebar-navigation/sidebar-navigation-group.component';
import {
  BACKOFFICE_PRIMARY_GROUP,
  BACKOFFICE_SECONDARY_GROUP,
  BackofficeSidebarAction,
} from '@shared/sidebar-navigation/sidebar-navigation.config';

@Component({
  selector: 'app-sidebar-backoffice',
  standalone: true,
  imports: [
    NgClass,
    FontAwesomeModule,
    SidebarNavigationGroupComponent,
  ],
  templateUrl: './sidebar-backoffice.component.html',
  styleUrl: './sidebar-backoffice.component.css',
})
export class SidebarBackofficeComponent implements OnInit, OnDestroy {
  readonly primaryNavGroup = BACKOFFICE_PRIMARY_GROUP;
  readonly secondaryNavGroup = BACKOFFICE_SECONDARY_GROUP;

  isMobile = false;
  isOpen = true;
  isMenuExpanded = false;
  activeMenuSection: 'home' | 'profile' | 'payments' | null = 'home';
  private subscriptions: Subscription[] = [];

  // Información del usuario actual
  userInfo: {
    name: string;
    email: string;
    profile_image_url?: string;
    roles: Array<{
      id: number;
      name: string;
      guard_name: string;
    }>;
  } = {
    name: '',
    email: '',
    profile_image_url: '',
    roles: [],
  };

  faHome = faHome;
  faUserRegular = faUserRegular;
  faChevronLeft = faChevronLeft;
  faChevronRight = faChevronRight;
  faBarsSort = faBarsSort;
  faCreditCard = faCreditCard;
  faGear = faGear;

  constructor(
    private router: Router,
    private collapsibleMenuStateService: CollapsibleMenuStateService,
    private authService: AuthenticationService,
    private sidebarService: SidebarService,
    private navigationShellState: NavigationShellStateService,
  ) {
    this.subscriptions.push(
      this.authService.currentUser$.subscribe((userData) => {
        if (userData) {
          this.userInfo = {
            name: userData.name || '',
            email: userData.email || '',
            profile_image_url: userData.profile_image_url || '',
            roles: userData.roles || [],
          };
        }
      })
    );

    this.subscriptions.push(
      this.navigationShellState.sidebarMenuState$.subscribe((state) => {
        this.isMobile = state.isMobile;
        this.isOpen = state.isSidebarOpen;
        this.isMenuExpanded = state.isMenuExpanded;

        if (state.isMobile && state.isMenuExpanded) {
          this.collapsibleMenuStateService.setMenuExpanded(false);
        }

        if (state.isMobile && state.isSidebarOpen) {
          this.sidebarService.closeSidebar();
        } else if (!state.isMobile && !state.isSidebarOpen) {
          this.sidebarService.toggleSidebar();
        }
      }),
    );
  }

  ngOnInit(): void {
    this.updateActiveMenuFromRoute();

    // Escuchar cambios de ruta para actualizar el menú activo
    this.subscriptions.push(
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd)
      ).subscribe(() => {
        this.updateActiveMenuFromRoute();
      })
    );

  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  private updateActiveMenuFromRoute(): void {
    const url = this.router.url;
    
    if (url.includes('/home-backoffice')) {
      this.activeMenuSection = 'home';
    } else if (url.includes('/profile') && (url.includes('mode=backoffice') || url.includes('backoffice=true'))) {
      this.activeMenuSection = 'profile';
    } else if (url.includes('/profile') && sessionStorage.getItem('login_destination') === 'backoffice') {
      this.activeMenuSection = 'profile';
    } else if (url.includes('/payments-invoices-backoffice')) {
      this.activeMenuSection = 'payments';
    } else {
      this.activeMenuSection = 'home'; // Por defecto en backoffice
    }
  }

  onNavigationItemClick(action: BackofficeSidebarAction): void {
    switch (action) {
      case 'home':
        this.openHome();
        break;
      case 'profile':
        this.openProfile();
        break;
      case 'payments':
        this.openPayments();
        break;
      case 'settings':
        this.openSettings();
        break;
    }
  }

  // Métodos del menú - solo Inicio y Perfil - completamente separados
  openHome(): void {
    this.activeMenuSection = 'home';
    this.router.navigate(['/dashboard/home-backoffice']);

    if (this.isMobile && this.isMenuExpanded) {
      this.collapsibleMenuStateService.setMenuExpanded(false);
    }
  }

  openProfile(): void {
    this.activeMenuSection = 'profile';
    // Navegar a una ruta de perfil específica para backoffice o usar query params
    this.router.navigate(['/dashboard/profile'], { queryParams: { mode: 'backoffice' } });

    if (this.isMobile && this.isMenuExpanded) {
      this.collapsibleMenuStateService.setMenuExpanded(false);
    }
  }

  openPayments(): void {
    this.activeMenuSection = 'payments';
    this.router.navigate(['/dashboard/payments-invoices-backoffice']);

    if (this.isMobile && this.isMenuExpanded) {
      this.collapsibleMenuStateService.setMenuExpanded(false);
    }
  }

  openSettings(): void {
    this.router.navigate(['/dashboard/settings', 'general'], { queryParams: { mode: 'backoffice' } });

    if (this.isMobile && this.isMenuExpanded) {
      this.collapsibleMenuStateService.setMenuExpanded(false);
    }
  }

  // Método para alternar el menú expandido
  toggleMenuExpanded(): void {
    this.collapsibleMenuStateService.toggleMenuExpanded();
  }

  // Método para alternar el sidebar
  toggleSidebar(): void {
    this.sidebarService.toggleSidebar();
  }
}
